import { getNestedValue, fieldValueEqual } from '@dashboard/components/Formik/formUtils';

const COLOR_TOKEN_SOURCE_PATHS = [
  'variables.tokens.colorPrimary',
  'variables.tokens.colorSecondary',
  'variables.tokens.colorBackground',
  'variables.tokens.colorSurface',
  'variables.tokens.colorSurfaceAccent',
  'variables.tokens.textPrimary',
  'variables.tokens.textSecondary',
  'variables.tokens.textTertiary',
  'variables.elements.checkout.form.priceBreakdown.styles.color',
  'variables.elements.checkout.cards.styles.backgroundColor',
  'variables.elements.checkout.cards.styles.borderColor',
  'variables.elements.checkout.cards.selectedStyles.backgroundColor',
  'variables.elements.checkout.cards.selectedStyles.borderColor',
  'variables.elements.checkout.cards.detail.styles.color',
  'variables.elements.checkout.cards.name.styles.color',
  'variables.elements.checkout.cards.description.styles.color',
  'variables.elements.checkout.cards.price.styles.color',
  'variables.elements.checkout.cards.discountedFromPrice.styles.color',
  'variables.elements.checkout.cards.groupName.styles.color',
  'variables.elements.checkout.cards.badge.styles.color',
  'variables.elements.checkout.cards.badge.styles.backgroundColor',
  'variables.elements.checkout.cards.badge.styles.borderColor',
  // The bump highlight's icon swatch follows its text colour.
  'variables.elements.checkout.form.orderBumps.badge.styles.color',
  // The call out swatches follow the title (text and icon), the card (border)
  // and a wash of the text over the card background.
  'variables.elements.checkout.form.orderBumps.title.styles.color',
  'variables.elements.checkout.form.orderBumps.styles.backgroundColor',
  'variables.elements.checkout.form.orderBumps.styles.borderColor',
  'variables.elements.checkout.form.orderBumps.callout.styles.color',
  // The cover processing fees amount swatch follows that box's label.
  'variables.elements.checkout.form.coverProcessingFees.label.styles.color',
  'variables.layout.centerLayout.contentBackgroundColor',
  'variables.layout.doubleLayout.contentBackgroundColor',
  'variables.layout.splitLayout.contentBackgroundColor',
  // Fields + base PWYW colors feed the PWYW swatch chains (base input/label
  // inherit from Fields; the price-option and variant subsections inherit
  // from the base) — without these, an inherited swatch in another section
  // goes stale until a page refresh. The part-row comparator only re-checks
  // its PRIMARY swatch, so the nested Focus/Placeholder cells rely on this
  // list entirely.
  'variables.elements.checkout.form.fields.input.styles.color',
  'variables.elements.checkout.form.fields.input.styles.backgroundColor',
  'variables.elements.checkout.form.fields.input.styles.borderColor',
  'variables.elements.checkout.form.fields.input.focusStyles.color',
  'variables.elements.checkout.form.fields.input.focusStyles.backgroundColor',
  'variables.elements.checkout.form.fields.input.focusStyles.borderColor',
  'variables.elements.checkout.form.fields.input.placeholderStyles.color',
  'variables.elements.checkout.form.fields.label.styles.color',
  'variables.elements.checkout.form.fields.label.focusStyles.color',
  'variables.elements.checkout.pwyw.input.styles.color',
  'variables.elements.checkout.pwyw.input.styles.backgroundColor',
  'variables.elements.checkout.pwyw.input.styles.borderColor',
  'variables.elements.checkout.pwyw.input.focusStyles.color',
  'variables.elements.checkout.pwyw.input.focusStyles.backgroundColor',
  'variables.elements.checkout.pwyw.input.focusStyles.borderColor',
  'variables.elements.checkout.pwyw.input.placeholderStyles.color',
  'variables.elements.checkout.pwyw.label.styles.color',
  'variables.elements.checkout.pwyw.label.focusStyles.color',
  // The Interval / Currency suffix swatches follow the base suffix + each
  // context's PWYW input placeholder (see resolveInheritedDefault).
  'variables.elements.checkout.pwyw.suffix.styles.color',
  'variables.elements.product.pricePicker.option.pwywInput.placeholderStyles.color',
  'variables.elements.checkout.variants.variantOptions.pwywInput.placeholderStyles.color',
  'variables.elements.checkout.form.orderBumps.pwywInput.placeholderStyles.color',
  'variables.elements.product.pricePicker.option.label.styles.color',
  'variables.elements.product.pricePicker.option.styles.color',
  'variables.elements.product.pricePicker.option.description.styles.color',
  'variables.elements.product.pricing.price.styles.color',
  'variables.elements.product.pricePicker.option.price.styles.color',
];

function colorTokenSourcesEqual(prevValues, nextValues) {
  for (const path of COLOR_TOKEN_SOURCE_PATHS) {
    if (!fieldValueEqual(getNestedValue(prevValues, path), getNestedValue(nextValues, path))) {
      return false;
    }
  }
  return true;
}

/**
 * Design-specific memoization comparator for field props.
 * Returns true (equal), false (changed), or undefined (no opinion / not a design field).
 */
export function designFieldPropsAreEqual(prevProps, nextProps) {
  const pf = prevProps.field;
  const nf = nextProps.field;

  // For nameless fields (headings, label-toggles, collapsible fieldgroups),
  // check design-specific props
  if (!pf.name || !nf.name) {
    if (pf.canReset !== nf.canReset) return false;
    if (pf.canToggle !== nf.canToggle) return false;
    if (pf.resetTier !== nf.resetTier) return false;
    if (pf.isAtDefault !== nf.isAtDefault) return false;
    if (pf.defaultOpen !== nf.defaultOpen) return false;
    // No opinion on the rest — let generic comparator handle it
    return undefined;
  }

  // For font-weight-select, check the font family dependency
  if (nf.type === 'font-weight-select' && nf.fontFamilyFieldName) {
    const prevFontFamily = getNestedValue(prevProps.values, pf.fontFamilyFieldName);
    const nextFontFamily = getNestedValue(nextProps.values, nf.fontFamilyFieldName);
    if (prevFontFamily !== nextFontFamily) return false;
  }

  // For color fields, check if any source color token changed
  if (nf.type === 'color-select-dual' || nf.type === 'color-group-with-fonts') {
    if (!colorTokenSourcesEqual(prevProps.values, nextProps.values)) return false;
    // Re-render when defaultColors changes (e.g. seller.brandColor loaded after auth)
    if (
      pf.defaultColors?.light !== nf.defaultColors?.light ||
      pf.defaultColors?.dark !== nf.defaultColors?.dark
    ) {
      return false;
    }
  }

  // For color-group-with-fonts, also check embedded font sub-field values.
  // fontGroupHasChanges is a boolean that only flips once (false→true) and stays true,
  // so it can't detect individual value changes after the first modification.
  if (nf.type === 'color-group-with-fonts' && nf.fontGroupFields) {
    for (const f of nf.fontGroupFields) {
      if (!f?.name) continue;
      const prev = getNestedValue(prevProps.values, f.name);
      const next = getNestedValue(nextProps.values, f.name);
      if (!fieldValueEqual(prev, next)) return false;
    }
  }

  // For part-row, refresh when any source token changes (so the row's primary
  // swatch updates), when the primary's resolved default colors change, or when
  // any bound path's value changes (covers nested field edits inside eyebrows).
  if (nf.type === 'part-row') {
    if (!colorTokenSourcesEqual(prevProps.values, nextProps.values)) return false;

    const prevPrimaryDefaults = pf.primaryColorField?.defaultColors;
    const nextPrimaryDefaults = nf.primaryColorField?.defaultColors;
    if (
      prevPrimaryDefaults?.light !== nextPrimaryDefaults?.light ||
      prevPrimaryDefaults?.dark !== nextPrimaryDefaults?.dark
    ) {
      return false;
    }

    if (Array.isArray(nf.paths)) {
      for (const p of nf.paths) {
        if (
          !fieldValueEqual(getNestedValue(prevProps.values, p), getNestedValue(nextProps.values, p))
        ) {
          return false;
        }
      }
    }

    if (pf.resetTier !== nf.resetTier) return false;
    if (pf.isAtDefault !== nf.isAtDefault) return false;
    if (pf.enabled !== nf.enabled) return false;
    if (pf.currentColorMode !== nf.currentColorMode) return false;
  }

  // Check design-specific field config properties
  if (pf.resetTier !== nf.resetTier) return false;
  if (pf.isAtDefault !== nf.isAtDefault) return false;
  if (pf.groupEnabled !== nf.groupEnabled) return false;
  if (pf.groupResetTier !== nf.groupResetTier) return false;
  if (pf.groupIsAtDefault !== nf.groupIsAtDefault) return false;
  if (pf.currentColorMode !== nf.currentColorMode) return false;
  if (pf.fontGroupHasChanges !== nf.fontGroupHasChanges) return false;

  return undefined; // No opinion — let generic comparator continue
}

/**
 * Extra leaf field names to track for design-specific container structures.
 */
export function getDesignLeafFieldNames(field) {
  const names = [];
  if (field.toggleKey) names.push(field.toggleKey);
  if (field.groupToggleKey) names.push(field.groupToggleKey);
  if (field.fontGroupFields && Array.isArray(field.fontGroupFields)) {
    for (const f of field.fontGroupFields) {
      if (f && f.name) names.push(f.name);
    }
  }
  if (field.type === 'part-row' && Array.isArray(field.paths)) {
    for (const p of field.paths) {
      if (typeof p === 'string') names.push(p);
    }
  }
  return names;
}

/**
 * Extra container-children structure checks for design-specific props.
 */
export function designContainerChildrenPropsEqual(pf, nf) {
  if (pf == null || nf == null) return pf === nf;
  if (pf.enabled !== nf.enabled) return false;
  if (pf.groupEnabled !== nf.groupEnabled) return false;
  if (pf.currentColorMode !== nf.currentColorMode) return false;
  if (pf.defaultOpen !== nf.defaultOpen) return false;
  if (pf.isRelevant !== nf.isRelevant) return false;
  if (pf.autoClose !== nf.autoClose) return false;

  // Color-select-dual children resolve their swatch's defaultColors from
  // sibling values (e.g. button text color reads button background). The
  // parent container otherwise wouldn't notice the bg→text dependency.
  if (pf.type === 'color-select-dual' || pf.type === 'color-group-with-fonts') {
    if (
      pf.defaultColors?.light !== nf.defaultColors?.light ||
      pf.defaultColors?.dark !== nf.defaultColors?.dark
    ) {
      return false;
    }
  }

  // Recurse into nested container children (e.g. conditional-wrapper → fieldgroups)
  // so that changes like isRelevant on grandchildren are detected.
  const prevNested = pf.children || pf.fields;
  const nextNested = nf.children || nf.fields;
  if (Array.isArray(prevNested) && Array.isArray(nextNested)) {
    if (prevNested.length !== nextNested.length) return false;
    for (let i = 0; i < nextNested.length; i++) {
      if (!designContainerChildrenPropsEqual(prevNested[i], nextNested[i])) return false;
    }
  }

  return true;
}

/**
 * Extra container descendant checks for design fields.
 * Returns false if color token sources changed, undefined otherwise.
 */
export function designContainerValuesEqual(prevValues, nextValues) {
  if (!colorTokenSourcesEqual(prevValues, nextValues)) return false;
  return undefined;
}
